/**
 * Journal post content.
 *
 * TO ADD A NEW POST: append an object to the `posts` array below. Newest
 * posts should go first — the index page renders them in array order.
 * Everything the index card and the post page need lives in this one object,
 * so no component changes are required for a new entry.
 */

/** A run of text. Add `to` for an internal link, `href` for an external one,
 *  `bold` to emphasize, or `italic` for a signoff. */
export type Segment =
  | string
  | {
      text: string
      /** Internal route path, e.g. '/menu'. Rendered as a react-router Link. */
      to?: string
      /** External URL. Opens in a new tab with rel="noopener noreferrer". */
      href?: string
      bold?: boolean
      italic?: boolean
    }

export type Block =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; content: Segment[] }
  | { type: 'list'; items: Segment[][] }

export type Post = {
  slug: string
  /** Displayed on the page and in cards. */
  title: string
  /** Human-readable date, e.g. 'July 28, 2026'. */
  date: string
  /** ISO 8601 date for the Article schema's datePublished. */
  datePublished: string
  author: string
  /** Short summary for the index card. */
  excerpt: string
  /** <title> for the post page. */
  metaTitle: string
  /** meta[name=description] for the post page. */
  metaDescription: string
  body: Block[]
  /** Closing italic byline, rendered below the body. */
  byline?: string
}

export const posts: Post[] = [
  {
    slug: 'our-story',
    title: 'The Story of Grounded',
    date: 'July 30, 2026',
    datePublished: '2026-07-30',
    author: 'Grounded',
    excerpt:
      "We're a small, local family who wanted to create a space where neighbors could come together over a delicious cup of coffee. This is our story.",
    metaTitle:
      'The Story of Grounded - A Family Coffee Shop in Livermore, CA',
    metaDescription:
      "The story of Grounded - a small local family who opened a Livermore coffee shop as a place for neighbors to gather over great coffee, inspired by the community cafes they've loved.",
    body: [
      {
        type: 'paragraph',
        content: [
          'The origin story for Grounded is simple. We\u2019re a small, local family who wanted to create a space where neighbors could come together and celebrate our community over a delicious cup of coffee.',
        ],
      },
      {
        type: 'paragraph',
        content: [
          'There was no grand plan. In fact, neither of us came from the coffee industry. Our north star was simply the memories of all the wonderful local coffee shops we\u2019ve visited whenever we traveled. Cozy, welcoming spaces. No master design, but a unique perspective of the town they\u2019re embodied within.',
        ],
      },
      {
        type: 'paragraph',
        content: ['We think of coffee shops like:'],
      },
      {
        type: 'list',
        items: [
          [
            {
              text: 'Third Space Coffeehouse in Bonneville, KS',
              href: 'https://thirdspacecoffeehouse.com/',
            },
          ],
          [
            {
              text: 'Downtown Local in Pescadero, CA',
              href: 'https://www.yelp.com/biz/downtown-local-pescadero-2',
            },
          ],
          [
            {
              text: 'Hidden House Coffee in Santa Ana, CA',
              href: 'https://hiddenhousecoffee.com/',
            },
          ],
          [
            {
              text: 'Crema in Nashville, TN',
              href: 'https://crema-coffee.com/',
            },
          ],
          [
            {
              text: "Enoch's in Westside Manhattan, NY",
              href: 'https://enochs.nyc/',
            },
          ],
        ],
      },
      {
        type: 'paragraph',
        content: [
          'Each funky, eclectic, and just a perfect encapsulation of the community that surrounds it. And of course, we\u2019ve had our minds blown at specialty, third-wave shops like:',
        ],
      },
      {
        type: 'list',
        items: [
          [
            {
              text: 'Iwana Coffee in Las Vegas, NV',
              href: 'https://iwanacoffee.com/',
            },
          ],
          [
            {
              text: 'Kanso Coffee Lab in Munich, Germany',
              href: 'https://kansocoffeelab.com/',
            },
          ],
          [
            {
              text: 'Coffee Stain in Kuala Lumpur, MY',
              href: 'https://www.coffeestain.my/',
            },
          ],
        ],
      },
      {
        type: 'paragraph',
        content: [
          'Those were our inspiration and the driver to open Grounded Livermore.',
        ],
      },
      {
        type: 'paragraph',
        content: [
          'In Grounded, we wanted a communal space where good company is met by high-quality coffee and nourishment. We sought to create a place where neighbors engage, and the artisanal talents of our community are showcased.',
        ],
      },
      {
        type: 'paragraph',
        content: [
          'Like all that is real in the world, our shop is not perfect. But that\u2019s almost exactly the intent. Our lives are not perfect. That\u2019s what makes us a community. We embrace, we celebrate, and we support each other through our successes and our imperfections.',
        ],
      },
      {
        type: 'paragraph',
        content: [{ text: '#StayGrounded', bold: true }],
      },
      {
        type: 'paragraph',
        content: [{ text: '\u2014 The Kungs', italic: true }],
      },
    ],
  },
  {
    slug: 'best-coffee-in-livermore',
    title: "The Best Coffee in Livermore: A Local's Guide",
    date: 'July 28, 2026',
    datePublished: '2026-07-28',
    author: 'Grounded',
    excerpt:
      "What makes a neighborhood coffee shop worth your morning, what to order and when, and where to find us — a local's guide to great coffee in Livermore.",
    metaTitle: "The Best Coffee in Livermore: A Local's Guide | Grounded",
    metaDescription:
      "A local's guide to finding great coffee in Livermore, CA - what makes a neighborhood coffee shop worth your morning, what to order, and where to go. From the team at Grounded.",
    byline:
      'Written by the team at Grounded - a neighborhood coffee shop in Livermore, CA.',
    body: [
      {
        type: 'paragraph',
        content: [
          "Livermore mornings have a particular kind of magic. The light comes up over the vineyards, the air still has a little of the valley's overnight cool in it, and the whole town seems to move at the pace of a first cup of coffee. If you're looking for where to spend that cup - whether you're new to town, showing a friend around, or just tired of your kitchen drip - here's how we think about great coffee in Livermore, from people who make it every day.",
        ],
      },
      {
        type: 'heading',
        text: 'What makes a coffee shop worth your morning',
      },
      {
        type: 'paragraph',
        content: [
          "A good cup matters. But if a great cup were the whole story, you'd buy a nicer machine and never leave the house. What actually pulls people out the door, morning after morning, is the place - somewhere that feels like the living room of the neighborhood, where the barista starts your drink when they see you park. The coffee is the reason you come the first time. The community is the reason you come back.",
        ],
      },
      {
        type: 'heading',
        text: 'How we think about the coffee itself',
      },
      {
        type: 'paragraph',
        content: [
          "For the record, the cup should be excellent too. Here's what we look for - and how we do it at Grounded:",
        ],
      },
      {
        type: 'list',
        items: [
          [
            { text: 'Beans with a point of view.', bold: true },
            ' We pour Proyecto Diaz on our drip - a bright, balanced medium-light roast - and pull espresso with Chromatic Coffee\u2019s "Gamut" roast. Our pour-overs rotate through partner roasters.',
          ],
          [
            { text: 'Drinks made with care, not just speed.', bold: true },
            " A flat white and a latte are not the same drink, and a cortado isn't a small latte.",
          ],
          [
            { text: 'Something for the non-coffee crowd, too.', bold: true },
            ' Great matcha, a proper chai, a house lavender lemonade for the afternoon.',
          ],
        ],
      },
      {
        type: 'heading',
        text: 'What to order, and when',
      },
      {
        type: 'paragraph',
        content: [
          "If you're not sure where to start, here's a local's cheat sheet:",
        ],
      },
      {
        type: 'list',
        items: [
          [
            { text: 'First thing in the morning:', bold: true },
            ' a cortado or flat white for espresso-forward and quick, or a cafe au lait for something gentler.',
          ],
          [
            { text: 'Mid-morning, with food:', bold: true },
            ' a latte and a Breakfast Sando - bacon, egg, and cheese on ciabatta with a little Yellowbird sriracha.',
          ],
          [
            { text: 'Afternoon pick-me-up:', bold: true },
            ' cold brew, or our Vietnamese cold brew if you want it rich and a little sweet.',
          ],
          [
            { text: 'Bringing a crowd:', bold: true },
            ' a to-go box of drip that serves about ten.',
          ],
        ],
      },
      {
        type: 'paragraph',
        content: [
          'You can see the full lineup and order ahead on our ',
          { text: 'menu', to: '/menu' },
          '.',
        ],
      },
      {
        type: 'heading',
        text: 'More than coffee - a place to gather',
      },
      {
        type: 'paragraph',
        content: [
          'The best neighborhood coffee shops end up being more than cafes. They host the first-date coffees and the standing Friday catch-ups, the laptop mornings and the after-school snacks. That\u2019s the part we care about most - it\u2019s why "coffee is community" isn\u2019t just a line on our wall. It\u2019s also why we bring the same experience out into Livermore: if you\u2019re planning a corporate meeting, a wedding, or a community gathering, our ',
          { text: 'catering', to: '/catering' },
          ' team can set up a coffee bar that makes your event feel a little more human.',
        ],
      },
      {
        type: 'heading',
        text: 'Come find us',
      },
      {
        type: 'paragraph',
        content: [
          'Grounded is tucked into the Wright Brothers Ave business park near the Livermore Municipal Airport, with free parking right outside the door and an easy stop on your way to or from the 580. We\u2019re open daily, 7am-2pm - come in for a slow morning or a quick pickup, and say hi. Whatever your order ends up being, we hope you find your spot in Livermore. If you\u2019re still looking, ',
          { text: "we'd love for it to be us", to: '/location' },
          '.',
        ],
      },
    ],
  },
]

export function getPostBySlug(slug: string): Post | undefined {
  return posts.find((post) => post.slug === slug)
}

/** Posts newest-first. The index page uses this so ordering never depends on
 *  where a new entry was pasted into the array. */
export function getPostsNewestFirst(): Post[] {
  return [...posts].sort((a, b) =>
    b.datePublished.localeCompare(a.datePublished),
  )
}
